import dayjs from 'dayjs';
import {
    FacilityTransferBlockedReason,
    FacilityTransferResult,
    FacilityTransferKind,
    FacilityTransferLockerBlockedReason,
    FacilityTransferPreview,
    FacilityTransferPreviewParams,
    FacilityTransferRecord,
    IFacilitySubscription,
} from './types';

/*
 * 시설 이용권 양도 — 화면이 쓰는 순수 판정 모음.
 * 날짜·일수 계산은 여기서 하지 않는다. 남은 일수·바뀐 종료일·사본 기간은 전부 서버 미리보기 값이다
 * (rappo docs/FACILITY_SUBSCRIPTION_TRANSFER_PLAN.md 3-2). 여기서는 문구와 분기만 정한다.
 */

export const FACILITY_TRANSFER_CODES = {
    SOURCE_NOT_ACTIVE: 'FACILITY_TRANSFER_SOURCE_NOT_ACTIVE',
    SOURCE_PAUSED: 'FACILITY_TRANSFER_SOURCE_PAUSED',
    NO_REMAINING_DAYS: 'FACILITY_TRANSFER_NO_REMAINING_DAYS',
    DAYS_OUT_OF_RANGE: 'FACILITY_TRANSFER_DAYS_OUT_OF_RANGE',
    START_DATE_PAST: 'FACILITY_TRANSFER_START_DATE_PAST',
    SAME_MEMBER: 'FACILITY_TRANSFER_SAME_MEMBER',
    TARGET_MEMBER_NOT_FOUND: 'FACILITY_TRANSFER_TARGET_MEMBER_NOT_FOUND',
    LOCKER_NOT_MOVABLE: 'FACILITY_TRANSFER_LOCKER_NOT_MOVABLE',
    KEY_REUSED: 'FACILITY_TRANSFER_KEY_REUSED',
    SUBSCRIPTION_CHANGED: 'FACILITY_SUBSCRIPTION_CHANGED',
    VOIDED: 'FACILITY_TRANSFER_VOIDED',
    INCOMPLETE: 'FACILITY_TRANSFER_INCOMPLETE',
    RECORD_MISSING: 'FACILITY_TRANSFER_RECORD_MISSING',
    REFUND_ACK_REQUIRED: 'FACILITY_TRANSFER_REFUND_ACK_REQUIRED',
    /** 같은 요청 키가 이미 실패로 끝남 — 새 키로 다시 접수해야 한다 */
    KEY_FAILED: 'FACILITY_TRANSFER_KEY_FAILED',
    INVALID_START_DATE: 'FACILITY_TRANSFER_INVALID_START_DATE',
    /** 이용권 수정: 저장될 시작·종료 시각이 거꾸로이거나 같다 */
    INVALID_PERIOD: 'FACILITY_SUBSCRIPTION_INVALID_PERIOD',
} as const;

/** 이용권 수정 실패 문구. 기간 오류는 서버 문구가 없어도 알아들을 문장으로 바꾼다. */
export const describeSubscriptionUpdateError = (error: any, fallback: string): string => {
    if (error?.body?.errorCode === FACILITY_TRANSFER_CODES.INVALID_PERIOD) {
        return '종료일은 시작일보다 뒤여야 해요. 기간을 다시 확인해 주세요.';
    }
    return error?.serverMessage || error?.message || fallback;
};

/** 원본 자체가 막혀 입력을 받을 수 없는 사유 */
const SOURCE_BLOCKERS: string[] = [
    FACILITY_TRANSFER_CODES.SOURCE_NOT_ACTIVE,
    FACILITY_TRANSFER_CODES.SOURCE_PAUSED,
    FACILITY_TRANSFER_CODES.NO_REMAINING_DAYS,
];

export const isSourceBlocker = (reason: string | null | undefined): boolean =>
    !!reason && SOURCE_BLOCKERS.includes(reason);

export const describeSourceBlocker = (reason: string | null | undefined): { title: string; message: string } | null => {
    switch (reason) {
        case FACILITY_TRANSFER_CODES.SOURCE_PAUSED:
            return {
                title: '정지 중인 이용권이에요',
                message: '정지 중이거나 정지 예약이 있으면 양도할 수 없어요.\n이용권 수정 창에서 정지를 해제한 뒤 다시 양도해 주세요.',
            };
        case FACILITY_TRANSFER_CODES.NO_REMAINING_DAYS:
            return { title: '남은 기간이 없어요', message: '오늘 이후로 쓸 수 있는 날이 없어 양도할 수 없어요.' };
        case FACILITY_TRANSFER_CODES.SOURCE_NOT_ACTIVE:
            return { title: '이용 중인 이용권이 아니에요', message: '만료·환불된 이용권은 양도할 수 없어요.' };
        default:
            return null;
    }
};

/** 입력 칸 옆에 붙일 사유 */
export const describeFieldBlocker = (
    reason: FacilityTransferBlockedReason | string | null | undefined,
): { field: 'recipient' | 'days' | 'startDate'; message: string } | null => {
    switch (reason) {
        case FACILITY_TRANSFER_CODES.SAME_MEMBER:
            return { field: 'recipient', message: '지금 이용권 주인에게는 넘길 수 없어요.' };
        case FACILITY_TRANSFER_CODES.TARGET_MEMBER_NOT_FOUND:
            return { field: 'recipient', message: '이 센터 회원이 아니거나 찾을 수 없는 회원이에요.' };
        case FACILITY_TRANSFER_CODES.DAYS_OUT_OF_RANGE:
            return { field: 'days', message: '남은 일수 안에서 골라 주세요.' };
        case FACILITY_TRANSFER_CODES.START_DATE_PAST:
            return { field: 'startDate', message: '시작일은 오늘부터 고를 수 있어요.' };
        case FACILITY_TRANSFER_CODES.INVALID_START_DATE:
            return { field: 'startDate', message: '없는 날짜예요. 시작일을 다시 골라 주세요.' };
        default:
            return null;
    }
};

export const describeLockerBlocked = (reason: FacilityTransferLockerBlockedReason | null | undefined): string | null => {
    switch (reason) {
        case 'PARTIAL_TRANSFER':
            return '기간 일부만 넘기면 칸은 원래 회원이 계속 써요. 받는 회원에겐 양도 후 다른 칸을 배정해 주세요.';
        case 'SLOT_SHARED':
            return '이 칸에 다른 이용권이 함께 걸려 있어 칸은 넘길 수 없어요.';
        case 'NO_LOCKER':
            return '배정된 칸이 없어 이용 기간만 넘어가요.';
        default:
            return null;
    }
};

export const FACILITY_TRANSFER_KIND_BADGE: Record<
    Exclude<FacilityTransferKind, null>,
    { label: string; bg: string; color: string; border: string }
> = {
    TARGET: { label: '받은 이용권', bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE' },
    CLOSED_SOURCE: { label: '넘긴 이용권', bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
    PARTIAL_SOURCE: { label: '일부 넘김', bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
};

/**
 * 화면에서 어떤 동작을 잠그는지(서버 잠금과 같은 범위). 표식이 없으면 아무것도 잠그지 않는다.
 * 받은 이용권을 다시 넘긴 경우 transferKind 는 넘긴 쪽으로 오고 isTransferTarget 이 true 다 —
 * 받은 이용권의 잠금(금액·환불·삭제)과 넘긴 이용권의 잠금을 합집합으로 건다.
 */
export const getFacilityTransferLocks = (
    subscription: Pick<IFacilitySubscription, 'transferKind' | 'isTransferTarget'> | null | undefined,
) => {
    const kind = subscription?.transferKind ?? null;
    const isTarget = kind === 'TARGET' || subscription?.isTransferTarget === true;
    const isClosed = kind === 'CLOSED_SOURCE';
    const isPartial = kind === 'PARTIAL_SOURCE';
    const reasons: string[] = [];
    if (isTarget) reasons.push('양도받은 이용권(0원)이라 금액·결제 정보는 바꿀 수 없고, 환불·삭제도 할 수 없어요.');
    if (isClosed) reasons.push('전부 양도한 이용권이라 메모만 고칠 수 있어요. 매출은 이 결제에 그대로 남아 있어요.');
    if (isPartial && !isTarget) reasons.push('기간 일부를 양도한 이용권이라 삭제할 수 없어요. 환불은 금액을 직접 적어서만 할 수 있어요.');
    if (isPartial && isTarget) reasons.push('기간 일부를 다시 양도한 이용권이에요.');
    return {
        kind,
        isTarget,
        /** 금액·결제일·결제수단 칸 */
        paymentLocked: isTarget || isClosed,
        /** 기간·락커 칸 */
        periodLocked: isClosed,
        canRefund: !isTarget && !isClosed,
        refundNeedsAck: isPartial && !isTarget,
        canDelete: kind === null && !isTarget,
        canPause: !isClosed,
        lockReason: reasons.length > 0 ? reasons.join('\n') : null,
    };
};

/** 열람 전용 계정인가(기존 판정과 같은 식: PayrollAuthGuard·회원 상세·브리핑). */
export const isCenterViewerAccount = (authObject: any): boolean =>
    authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';

/** 공통 클라이언트가 전송 전에 막은 오류인가(센터 열람 모드). 요청이 나가지 않았으므로 확정 실패다. */
export const isBlockedBeforeSend = (error: any): boolean =>
    !error?.status && typeof error?.message === 'string' && error.message.includes('센터 열람 모드');

/** 구서버(양도 경로 없음)의 404 인가. 이용권 없음 404 는 서버 메시지가 따로 있다. */
export const isFacilityTransferUnsupported = (error: any): boolean => {
    if (!error || Number(error.status) !== 404) return false;
    const body = error.body || {};
    if (body.errorCode) return false;
    const message = String(body.message ?? error.serverMessage ?? '');
    return message === 'Not found' || message === '';
};

export const getFacilityTransferErrorCode = (error: any): string | null => error?.body?.errorCode || null;

export type FacilityTransferSubmitFailure =
    /** 결과를 모른다 — 같은 키로 다시 보내야 한다 */
    | { kind: 'unknown'; message: string }
    /** 구서버 */
    | { kind: 'unsupported'; message: string }
    /**
     * 서버가 이 요청 키를 실패로 기록함(그 사이 바뀜·같은 키가 이미 실패) — 미리보기를 다시 읽고
     * **새 요청 키**로 입력 단계부터 다시 접수한다. 같은 키로 다시 보내면 KEY_FAILED 가 난다.
     */
    | { kind: 'changed'; message: string }
    /** 더 진행할 수 없음 — 창을 닫고 이용권을 다시 확인 */
    | { kind: 'fatal'; message: string }
    /** 입력을 고치면 되는 거절 */
    | { kind: 'rejected'; message: string };

/**
 * 실행 실패를 분류한다. 상태 코드가 없거나(망 오류·시간 초과) 5xx 면 서버에 반영됐는지 모르므로
 * 「같은 키로 다시 보내기」만 허용한다. 4xx 는 서버가 반영하지 않았다고 알려 준 것이다.
 */
export const classifyFacilityTransferFailure = (error: any): FacilityTransferSubmitFailure => {
    const status = Number(error?.status);
    const code = getFacilityTransferErrorCode(error);
    const serverMessage: string = error?.body?.message || error?.serverMessage || '';
    if (isBlockedBeforeSend(error)) {
        return { kind: 'fatal', message: '열람 전용 계정은 양도할 수 없어요.' };
    }
    if (isFacilityTransferUnsupported(error)) {
        return { kind: 'unsupported', message: '이 서버에서는 아직 이용권 양도를 쓸 수 없어요. 서버 업데이트 뒤에 다시 시도해 주세요.' };
    }
    if (!Number.isFinite(status) || status === 0 || status >= 500 || code === FACILITY_TRANSFER_CODES.INCOMPLETE) {
        return {
            kind: 'unknown',
            message: '처리 결과를 확인하지 못했어요. 「다시 보내기」를 누르면 같은 요청으로 이어서 처리되고, 두 번 양도되지 않아요.',
        };
    }
    if (code === FACILITY_TRANSFER_CODES.SUBSCRIPTION_CHANGED) {
        return { kind: 'changed', message: '그 사이 이용권이 바뀌어 양도하지 않았어요. 최신 내용으로 다시 확인해 주세요.' };
    }
    if (code === FACILITY_TRANSFER_CODES.KEY_FAILED) {
        return { kind: 'changed', message: '앞서 보낸 양도는 반영되지 않았어요. 최신 내용으로 다시 확인해 주세요.' };
    }
    if (code === FACILITY_TRANSFER_CODES.KEY_REUSED) {
        return {
            kind: 'fatal',
            message: '이 창에서 보낸 양도가 이미 다른 내용으로 처리됐어요. 창을 닫고 이용권을 다시 확인해 주세요.',
        };
    }
    if (code === FACILITY_TRANSFER_CODES.VOIDED || code === FACILITY_TRANSFER_CODES.RECORD_MISSING) {
        return { kind: 'fatal', message: serverMessage || '이 양도는 이어서 처리할 수 없어요.' };
    }
    if (status === 404 && !code) {
        return { kind: 'fatal', message: serverMessage || '이용권을 찾을 수 없어요. 삭제됐을 수 있어요.' };
    }
    if (status === 401 || status === 403) {
        return { kind: 'fatal', message: serverMessage || '양도할 권한이 없어요.' };
    }
    return { kind: 'rejected', message: serverMessage || '양도하지 못했어요. 입력을 확인해 주세요.' };
};

/**
 * 같은 키를 **다시** 보낸 요청(앞 요청의 결과를 모름)에서 보관 기록을 지워도 되는 응답인가.
 *
 * 서버 transferSubscription 은 원본 조회 직후 「같은 키 항목이 있으면 재생」을 맨 먼저 한다
 * (rappo facilitySubscriptionTransfer.service.ts, 689d5216 기준 780–781행). 그래서 그 뒤의 검증·원본 갱신이 던지는
 * 도메인 코드(FACILITY_TRANSFER_*·FACILITY_SUBSCRIPTION_*)는 「이 키로 반영된 양도가 없다」는 증거다.
 * 재생 경로 안에서 나는 코드 중 KEY_REUSED·VOIDED 는 이 키가 이미 종결됐다는 뜻이라 지워도 된다.
 *
 * 예외 둘은 지우지 않는다:
 *   - RECORD_MISSING: 재생 경로에서 원본에 같은 키 항목이 **있는데** 기록이 없을 때 난다(원본은 이미 줄었다).
 *   - INCOMPLETE(503): 원본은 줄었는데 마무리가 안 됐다 — 같은 키로 다시 보내야 이어서 완료된다.
 * 도메인 코드 없는 400(요청 검증)·401·403·라우트 404('Not found')·5xx·망 끊김·전송 전 차단도 증거가 아니다.
 */
const DOMAIN_CODE_PREFIXES = ['FACILITY_TRANSFER_', 'FACILITY_SUBSCRIPTION_'];
const KEY_STILL_OPEN_CODES: string[] = [FACILITY_TRANSFER_CODES.RECORD_MISSING, FACILITY_TRANSFER_CODES.INCOMPLETE];
export const isKeyTerminalResponse = (error: any): boolean => {
    const code = getFacilityTransferErrorCode(error);
    if (!code || KEY_STILL_OPEN_CODES.includes(code)) return false;
    if (Number(error?.status) >= 500) return false;
    return DOMAIN_CODE_PREFIXES.some((prefix) => code.startsWith(prefix));
};

/** 다시 보낸 요청이 종결 코드 아닌 이유로 실패했을 때의 안내(보관 유지, 같은 키로 다시) */
export const describeResendHeld = (error: any): string => {
    const status = Number(error?.status);
    const reason =
        status === 401 || error?.authRetrySkipped
            ? '로그인이 만료됐어요. 다시 로그인한 뒤'
            : status === 403 || isBlockedBeforeSend(error)
            ? '이 계정으로는 처리할 수 없어요. 권한 있는 계정으로'
            : '지금은 결과를 확인하지 못했어요. 잠시 뒤';
    return `${reason} 「다시 보내기」를 눌러 주세요. 앞서 보낸 요청은 그대로 보관해 두었고, 같은 요청으로 보내 두 번 양도되지 않아요.`;
};

/** 미리보기가 지금 입력에 대한 것인가(이전 입력의 응답을 보여 주며 진행하지 않게). */
export const isPreviewForInputs = (
    preview: { params: FacilityTransferPreviewParams; data: FacilityTransferPreview | null } | undefined,
    inputs: FacilityTransferPreviewParams,
): boolean => {
    if (!preview || !preview.data) return false;
    const a = preview.params;
    return (a.toMemberId || '') === (inputs.toMemberId || '') && a.days === inputs.days && (a.startDate || '') === (inputs.startDate || '');
};

/**
 * 양도 기록이 실제로 넘어간 것인가 — 판정은 이 함수 하나로 한다.
 *   'done'    : 완료(COMPLETED). 넘긴 기간 합산·환불 안내에 넣는다.
 *   'void'    : 반영 안 됨(FAILED)·되돌림(VOIDED). 넣지 않는다.
 *   'pending' : 처리 중·마무리 필요·알 수 없는 상태값. 완료로 보지 않고 「확인 필요」로 다룬다.
 * 화면 표시용 상태(displayStatus)가 있으면 그것을, 없으면(구서버) 기록 상태를 본다.
 */
export const classifyTransferRecord = (
    record: Pick<FacilityTransferRecord, 'status' | 'displayStatus'>,
): 'done' | 'void' | 'pending' => {
    const status = String(record.displayStatus || record.status || '');
    if (status === 'COMPLETED') return 'done';
    if (status === 'FAILED' || status === 'VOIDED') return 'void';
    return 'pending';
};

/**
 * 일부 양도 원본의 「누구에게 며칠」 목록(최근 것부터). 환불 창을 열 때 **새로 읽은 이력**에서 만든다 —
 * 창을 연 뒤 다른 관리자가 더 넘긴 기간이 낡은 이용권 객체(transferOuts)에는 없기 때문이다.
 * 이 이용권이 원본인 기록 중 완료된 것만 items 에 넣는다. 처리 중·상태 불명 기록이 있으면 pendingCount 로
 * 알려 주고 호출부가 환불을 막는다(넘긴 기간이 확정되지 않았다).
 */
export const describeTransferredForRefund = (
    subscriptionId: string,
    records: FacilityTransferRecord[] | undefined,
): {
    items: { transferId: string; days: number; at: string; toMemberName: string; isFull: boolean }[];
    pendingCount: number;
} => {
    const own = (records || []).filter((record) => String(record.sourceId) === String(subscriptionId));
    const items = own
        .filter((record) => classifyTransferRecord(record) === 'done')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((record) => ({
            transferId: String(record._id),
            days: Number(record.days) || 0,
            at: record.createdAt,
            isFull: record.isFull === true,
            toMemberName: record.toMemberName || '',
        }));
    return { items, pendingCount: own.filter((record) => classifyTransferRecord(record) === 'pending').length };
};

/** 이력의 락커 표시는 요청값이 아니라 실제 결과(lockerMoved, 구서버면 moveLocker)로 한다. */
export const didLockerMove = (record: Pick<FacilityTransferRecord, 'lockerMoved' | 'moveLocker'>): boolean =>
    typeof record.lockerMoved === 'boolean' ? record.lockerMoved : record.moveLocker === true;

/** 칸을 넘기려 했는데 실제로는 못 넘긴 양도(완료된 것) — 받는 회원에게 다른 칸을 배정해야 한다. */
export const needsLockerReassign = (record: Pick<FacilityTransferRecord, 'lockerMoved' | 'moveLocker' | 'status' | 'displayStatus'>): boolean =>
    record.moveLocker === true && record.lockerMoved === false && classifyTransferRecord(record) === 'done';

export const FACILITY_TRANSFER_DISPLAY_STATUS: Record<string, { label: string; color: string; bg: string }> = {
    COMPLETED: { label: '완료', color: '#047857', bg: '#ECFDF5' },
    PROCESSING: { label: '처리 중', color: '#1D4ED8', bg: '#EFF6FF' },
    NEEDS_COMPLETION: { label: '마무리 필요', color: '#B45309', bg: '#FFFBEB' },
    FAILED: { label: '반영 안 됨', color: '#6B7280', bg: '#F3F4F6' },
    VOIDED: { label: '되돌림', color: '#6B7280', bg: '#F3F4F6' },
};

/** 모르는 상태값은 「완료」로 대체하지 않는다. */
export const FACILITY_TRANSFER_UNKNOWN_STATUS = { label: '확인 필요', color: '#B45309', bg: '#FFFBEB' };
export const getTransferDisplayStatusStyle = (record: Pick<FacilityTransferRecord, 'status' | 'displayStatus'>) =>
    FACILITY_TRANSFER_DISPLAY_STATUS[String(record.displayStatus || record.status || '')] || FACILITY_TRANSFER_UNKNOWN_STATUS;

/** 양도 완료 알림 본문. 기간은 서버 응답(기록) 그대로다. */
export const buildTransferDoneMessage = (result: FacilityTransferResult, recipientName: string): string => {
    const record = result?.transfer;
    const name = recipientName || record?.toMemberName || '받는 회원';
    const lines: string[] = [];
    if (record) {
        lines.push(`${name}님께 ${record.days}일을 넘겼어요.`);
        lines.push(
            `받는 회원 이용권: ${dayjs(record.targetStartDate).format('YY.MM.DD')} ~ ${dayjs(record.targetEndDate).format('YY.MM.DD')} (0원)`,
        );
        // 칸을 넘기려 했어도 그 사이 다른 회원이 가져갔으면 칸 없이 만들어진다 — 실제 사본의 칸으로 판단한다.
        const lockerTaken = (result?.warnings || []).find((w) => w.code === 'LOCKER_NOT_MOVED_TAKEN') as any;
        if (lockerTaken) {
            lines.push(
                '',
                `넘기려던 락커 ${lockerTaken.lockerNumber ? `${lockerTaken.lockerNumber}번 ` : ''}칸을 그 사이 다른 회원이 쓰고 있어 칸 없이 넘어갔어요. 받는 회원에게 다른 칸을 배정해 주세요.`,
            );
        } else if (record.moveLocker && record.lockerMoved === false) {
            // 재전송 응답은 경고를 다시 싣지 않는다 — 실제 결과(lockerMoved)로 같은 안내를 만든다.
            lines.push('', '넘기려던 락커 칸은 그 사이 다른 회원이 써서 칸 없이 넘어갔어요. 받는 회원에게 다른 칸을 배정해 주세요.');
        } else if (record.moveLocker && result?.target?.lockerNumber) {
            lines.push(`락커 ${result.target.lockerNumber}번 칸도 함께 넘어갔어요.`);
        }
    } else {
        lines.push(`${name}님께 양도했어요.`);
    }
    if ((result?.warnings || []).some((w) => w.code === 'LOCKER_FIXED_LOCK_NUMBER')) {
        lines.push('', '이 칸은 고정 비밀번호라 원래 회원도 열 수 있어요. 「락커 관리」에서 비밀번호를 바꿔 주세요.');
    }
    return lines.join('\n');
};
